import React, { useEffect, useState } from 'react';
import { CalendarPlus, Copy, RotateCcw } from 'lucide-react';
import { getPlannerFeedToken, createPlannerFeedToken, resetPlannerFeedToken } from '../../api';

// "Add my schedule to my phone calendar" (Club Planner, Phase 6) — used in the player's Schedule
// page and in the staff week view. Creates (or shows) the user's personal feed link; the phone
// calendar subscribes to it and refreshes by itself. "Reset link" revokes the old link (anyone who
// had it gets nothing anymore) and makes a new one, which then has to be added again.

const btn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44, borderRadius: 10,
  border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)',
  fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none', padding: '0 14px',
};

export default function CalendarFeedLink({ token, audienceText }) {
  const [feed, setFeed] = useState(undefined); // undefined = loading, null = none yet
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!token) return;
    getPlannerFeedToken(token).then(setFeed).catch(() => setFeed(null));
  }, [token]);

  const create = async () => {
    setBusy(true);
    try { setFeed(await createPlannerFeedToken(token)); setMsg(null); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm('Make a new link? The old one stops working, so you have to add the calendar again on every device that uses it.')) return;
    setBusy(true);
    try { setFeed(await resetPlannerFeedToken(token)); setMsg('New link made. Add it to your calendar again.'); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(feed.url); setMsg('Link copied.'); } catch { setMsg(feed.url); }
  };

  if (feed === undefined) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!feed ? (
        <button type="button" style={btn} disabled={busy} onClick={create}><CalendarPlus size={16} /> Add my schedule to my phone calendar</button>
      ) : (
        <>
          <a href={feed.webcal_url} style={btn}><CalendarPlus size={16} /> Add to my phone calendar</a>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={{ ...btn, flex: 1, fontSize: 13 }} onClick={copy}><Copy size={14} /> Copy link</button>
            <button type="button" style={{ ...btn, flex: 1, fontSize: 13 }} disabled={busy} onClick={reset}><RotateCcw size={14} /> Reset link</button>
          </div>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
            iPhone: tap "Add to my phone calendar". Google Calendar / Android: copy the link and add it under Other calendars → From URL.
          </span>
        </>
      )}
      <span style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
        Personal feed · {audienceText} · updates automatically
      </span>
      {msg && <span role="status" style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', wordBreak: 'break-all' }}>{msg}</span>}
    </div>
  );
}
